import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import ProductGrid from "@/components/shop/ProductGrid";
import { Button } from "@/components/ui/button";
import { getCollectionBySlug, getCollectionsByCategory } from "@/data/collections";
import { products } from "@/data/products";

const Collection = () => {
  const { category, collection: collectionSlug } = useParams<{
    category: string;
    collection: string;
  }>();

  const collectionData = collectionSlug ? getCollectionBySlug(collectionSlug) : undefined;
  const otherCollections = category
    ? getCollectionsByCategory(category).filter((c) => c.slug !== collectionSlug)
    : [];

  // Filter products by collection
  const collectionProducts = useMemo(() => {
    if (!collectionData) return [];
    return products.filter(
      (product) =>
        product.collectionSlug === collectionData.slug ||
        product.collection.toLowerCase().includes(collectionData.id.toLowerCase())
    );
  }, [collectionData]);

  if (!collectionData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Collection Not Found</h1>
            <Button asChild>
              <Link to={`/products/${category || "hardwood"}`}>Back to Products</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        title={collectionData.name}
        subtitle={collectionData.longDescription}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Products", href: `/products/${category}` },
          { label: collectionData.category, href: `/products/${category}` },
          { label: collectionData.name },
        ]}
        backgroundImage={collectionData.heroImage}
        overlay
      />

      {/* Collection Features */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-6 md:gap-12"
          >
            {collectionData.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-foreground"
              >
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium">{feature}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
                {collectionData.name} Products
              </h2>
              <p className="text-muted-foreground mt-1">
                Showing {collectionProducts.length} products in this collection
              </p>
            </div>
            <Button asChild variant="outline">
              <Link to={`/products/${category}`} className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                All {collectionData.category}
              </Link>
            </Button>
          </div>

          <ProductGrid products={collectionProducts} />

          {collectionProducts.length === 0 && (
            <div className="text-center py-16 bg-muted rounded-xl">
              <h3 className="text-xl font-semibold mb-2">
                Products Coming Soon
              </h3>
              <p className="text-muted-foreground mb-6">
                We're currently adding products to this collection. Check back soon!
              </p>
              <Button asChild variant="coral">
                <Link to="/free-estimate">Get a Free Estimate</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Other Collections in Same Category */}
      {otherCollections.length > 0 && (
        <section className="py-12 md:py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
                Other {collectionData.category} Collections
              </h2>
              <p className="text-muted-foreground mt-2">
                Explore more {collectionData.category.toLowerCase()} options
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {otherCollections.map((col, index) => (
                <motion.div
                  key={col.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/products/${col.categorySlug}/${col.slug}`}
                    className="block group"
                  >
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4">
                      <img
                        src={col.heroImage}
                        alt={col.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-xs font-medium text-white/80 uppercase tracking-wider">
                          {col.subtitle}
                        </span>
                        <h3 className="text-lg font-semibold text-white">
                          {col.name}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-2xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Space?
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              Get a free in-home estimate with all-inclusive pricing. No hidden fees, no surprises.
            </p>
            <Button asChild size="lg" variant="coral">
              <Link to="/free-estimate">Get Your Free Estimate</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Collection;
